import { Navigate } from "react-router-dom";
import Login from "./pages/auth/Login";
import AuthLayout from "./layouts/Auth";
import DashboardLayout from "./layouts/Dashboard";
import Page404 from "./pages/auth/Page404";
import Overview from "./pages/Overview";
import Tasks from "./pages/Tasks";
import {
  Cases,
  CaseEvidencePage,
  DocumentsPage,
  EvidencePage,
  ContactDetailsPage,
  EvidenceDetailsPage,
  EdiscoveryContent,
  PreservationsContent,
  StorageSection,
  StorageListPage,
  StorageDetailsPage,
} from "./pages";
import CaseDetails from "./pages/Cases/CaseDetails/CaseDetails";
import InquirySection from "./pages/Inquiries/InquirySection";
import InquiryDetails from "./pages/Inquiries/InquiryDetails";
import EquipmentSection from "./pages/Equipment/EquipmentSection";
import SoftwareSection from "./pages/Software/SoftwareSection";
import LabAssetsSection from "./pages/LabAssets/LabAssetsSection";
import LabAssetDetails from "./pages/LabAssets/[asset_uuid]/LabAssetDetails";
import LocationsSection from "./pages/Locations/LocationsSection";
import LocationDetails from "./pages/Locations/LocationDetails";
import ClientsSection from "./pages/Clients/ClientsSection";
import ClientDetails from "./pages/Clients/[client_id]/ClientDetails";
import ContactsSection from "./pages/Contacts/ContactsSection";

import OfficesSection from "./pages/Offices/OfficesSection";

import Reports from "./pages/Reports/index";
import ReportDetails from "./pages/Reports/ReportDetails";

import AuthGuard from "./components/AuthGuard";
import ResetPassword from "./pages/auth/ResetPassword";
import SSOCallback from "./pages/auth/SSOCallback";
import AuditsPage from "./pages/Audits/AuditsPage";
import AuditDetailsPage from "./pages/Audits/AuditDetailsPage";
import TemplateEditor from "./pages/Settings/EditorTemplates/TemplateEditor";
import SystemSettings from "./pages/Settings/System";
import NotificationSettings from "./pages/Settings/NotificationSettings";
import OrgInfoSettings from "./pages/Settings/OrgInfo";
import EditorTemplatesSection from "./pages/Settings/EditorTemplates/EditorTemplates";
import DocumentTemplatesSection from "./pages/Settings/DocumentTemplates/DocumentTemplates";
import ItemNumberFormatSettings from "./pages/Settings/ItemNumberFormats";
import CaseTypeSettings from "./pages/Settings/CaseTypes";
import CaseStatusSettings from "./pages/Settings/CaseStatus";
import CaseProgressSettings from "./pages/Settings/CaseProgress";
import EvidenceTypeSettings from "./pages/Settings/EvidenceTypes";
import EvidenceProgressSettings from "./pages/Settings/EvidenceProgress";
import ItemLabelSettings from "./pages/Settings/ItemLabels";
import CustomFieldSettings from "./pages/Settings/CustomFieldSettings/index";
import TaskTemplateSettings from "./pages/Settings/TaskTemplates";
import TimeCategorySettings from "./pages/Settings/TimeEntryCategories";
import QaCheckListSettings from "./pages/Settings/QaCheckLists/QaCheckLists";
import QaIssueTypeSettings from "./pages/Settings/QaIssueTypes";
import AdminLog from "./pages/Settings/AdminLog";
import IntegrationSettings from "./pages/Settings/Integration/Integration";
import About from "./pages/Settings/About";
import CreateEditorTemplate from "./pages/Settings/EditorTemplates/CreateEditorTemplate";
import FprIntegration from "./pages/Settings/Integration/FPR_Integration/index";
import IntegrationsList from "./pages/Settings/Integration/IntegrationsList";
import MattermostIntegration from "./pages/Settings/Integration/MattermostIntegration/MattermostIntegration";

// import Loader from "./components/Loader";
import QaCheckList from "./pages/Settings/QaCheckLists/QaCheckList";
import ReleaseNotesSection from "./pages/Settings/ReleaseNotesSection";
import CaseOverview from "./pages/Cases/CaseOverview/CaseOverview";
import CaseAcquisition from "./pages/Cases/CaseAcquisitions/index";
import CaseTasks from "./pages/Cases/CaseTasks/index";
import CaseNotes from "./pages/Cases/CaseNotes/CaseNotes";
import NoteHistoryPage from "./pages/Cases/CaseHistory/NoteHistoryPage";
import CaseContacts from "./pages/Cases/CaseContacts/index";
import CaseQA from "./pages/Cases/CaseQA/index";
import QaDetails from "./pages/Cases/CaseQA/components/QaDetails";
import QaReviews from "./pages/Cases/CaseQA/components/QaReviews";
import CaseReports from "./pages/Cases/CaseReport/index";
import CaseReportDetails from "./pages/Cases/CaseReport/ReportDetail";
import ReportsAndArchives from "./pages/Cases/CaseReport/ReportsAndArchives";
import CaseActivity from "./pages/Cases/CaseActivity/index";
import TraceIndicators from "./pages/Cases/CaseAnalysis/sections/Trace-Indicators/Trace-Indicators";
import TraceAttributes from "./pages/Cases/CaseAnalysis/sections/Trace-Attributes/Trace-Attributes";

import CaseAnalysis from "./pages/Cases/CaseAnalysis/CaseAnalysis";

import Accounts from "./pages/Cases/CaseAnalysis/sections/Accounts/Accounts";

import TeamSection from "./pages/Users/TeamSection";
import UserRoles from "./pages/Users/components/UserRoles/UserRoles";
import UsersTabContent from "./pages/Users/components/UsersTabContent";
import UserDetails from "./pages/Users/components/UserDetails/UserDetails";
import GroupsTabContent from "./pages/Users/components/GroupsTabContent";

import { TimelineContent } from "./pages";

import MonolithSettings, {
  APIPage,
  CreateNewWebhookPage,
  WebhookDetailsPage,
  EditWebhookPage,
} from "./pages/Settings";
import {
  AccessRequests,
  BasicDetails,
  CustomFieldOptions,
  Instructions,
  ProfileContent,
  RelaySettingsPage,
  RelayUsers,
  UserManagement,
} from "./pages/Settings/relay";
import {
  M365ClientCredentialsPage,
  M365IntegrationPage,
} from "./pages/Settings/Integration/m365";
import { GoogleIntegration } from "./pages/Settings/Integration/google";
import { GoogleServiceAccountCredentialsPage } from "./pages/Settings/Integration/google/GoogleServiceAccountCredentialsPage";
import OfficeDetails from "./pages/Offices/[office_id]/OfficeDetails";

// All pages that rely on 3rd party components (other than Material-UI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

const unprotectedRoutes = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/auth/sso/callback",
    element: <SSOCallback />,
  },
  {
    path: "/password-reset/:uuid",
    element: <ResetPassword />,
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

const protectedRoutes = [
  {
    path: "/",
    element: <Navigate to="/dashboard/overview/user" />,
  },
  {
    path: "dashboard",
    element: <DashboardLayout />,
    children: [
      {
        path: "overview",
        element: <Navigate to="/dashboard/overview/user" />,
      },
      {
        path: "overview/:currentTab",
        element: <Overview />,
      },
      {
        path: "tasks",
        element: <Tasks />,
      },
      {
        path: "tasks/v/:item_id",
        element: <Tasks />,
      },
      {
        path: "tasks/:active_tab",
        element: <Tasks />,
      },
      {
        path: "tasks/:active_tab/:view",
        element: <Tasks />,
      },
    ],
  },
  {
    path: "cases",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Cases />,
      },
      {
        path: ":case_id",
        element: <CaseDetails />,
        children: [
          {
            path: "overview",
            element: <CaseOverview />,
          },
          {
            path: "evidence",
            element: <CaseEvidencePage />,
          },
          {
            path: "acquisitions",
            element: <CaseAcquisition />,
          },
          {
            path: "case-storage",
            element: <StorageSection />,
          },
          {
            path: "analysis",
            element: <CaseAnalysis />,
            children: [
              {
                path: "",
                element: <Navigate to="timeline" replace />,
              },
              {
                path: "timeline",
                element: <TimelineContent />,
              },
              {
                path: "timeline/:event_uuid",
                element: <TimelineContent />,
              },
              {
                path: "accounts",
                element: <Accounts />,
              },
              {
                path: "accounts/:uuid",
                element: <Accounts />,
              },
              {
                path: "trace-indicators",
                element: <TraceIndicators />,
              },
              {
                path: "trace-indicators/:uuid",
                element: <TraceIndicators />,
              },
              {
                path: "trace-attributes",
                element: <TraceAttributes />,
              },
              {
                path: "trace-attributes/:uuid",
                element: <TraceAttributes />,
              },
            ],
          },
          {
            path: "ediscovery",
            element: <EdiscoveryContent />,
            children: [
              {
                path: "",
                element: <Navigate to="preservations" replace />,
              },
              {
                path: "preservations",
                element: <PreservationsContent />,
              },
            ],
          },
          {
            path: "tasks",
            element: <CaseTasks />,
          },
          {
            path: "tasks/v/:item_id",
            element: <CaseTasks />,
          },
          {
            path: "notes",
            element: <CaseNotes />,
          },
          {
            path: "notes/:item_id",
            element: <CaseNotes />,
          },
          {
            path: "notes/:note_uuid/history", // New: generic route
            element: <NoteHistoryPage />,
          },
          {
            path: "notes/:note_uuid/history/:version_uuid",
            element: <NoteHistoryPage />,
          },
          {
            path: "documents",
            element: <DocumentsPage />,
            children: [
              {
                path: "folders/:folder_id",
                element: <DocumentsPage />,
              },
            ],
          },
          {
            path: "contacts",
            element: <CaseContacts />,
          },
          {
            path: "qa",
            element: <CaseQA />,
            children: [
              {
                path: "",
                element: <QaReviews />,
              },
              {
                path: "review/:review_id",
                element: <QaDetails />,
              },
            ],
          },
          {
            path: "reports",
            element: <CaseReports />,
            children: [
              {
                path: "",
                element: <ReportsAndArchives />,
              },
              {
                path: "generate-report/:case_report_id",
                element: <CaseReportDetails />,
              },
            ],
          },
          {
            path: "activity",
            element: <CaseActivity />,
          },
        ],
      },
    ],
  },
  {
    path: "inquiries",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <InquirySection />,
      },
      {
        path: ":inquiry_id",
        element: <InquiryDetails />,
      },
    ],
  },
  {
    path: "evidence",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <EvidencePage />,
      },
      {
        path: ":uuid/:active_tab",
        element: <EvidenceDetailsPage />,
      },
      {
        path: ":uuid/notes/:note_uuid/history", // New: generic route
        element: <NoteHistoryPage />,
      },
      {
        path: ":uuid/notes/:note_uuid/history/:version_uuid",
        element: <NoteHistoryPage />,
      },
    ],
  },
  {
    path: "audits",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <AuditsPage />,
      },
      {
        path: ":uuid/:active_tab",
        element: <AuditDetailsPage />,
      },
    ],
  },
  {
    path: "storage",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <StorageListPage />,
      },
      {
        path: ":storage_id",
        element: <StorageDetailsPage />,
      },
    ],
  },
  {
    path: "locations",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <LocationsSection />,
      },
      {
        path: ":location_id/details",
        element: <LocationDetails />,
      },
    ],
  },
  {
    path: "clients",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <ClientsSection />,
      },
      {
        path: ":client_id",
        element: <ClientDetails />,
      },
    ],
  },
  {
    path: "contacts",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <ContactsSection />,
      },
      {
        path: ":contact_id",
        element: <ContactDetailsPage />,
      },
    ],
  },
  {
    path: "assets",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <LabAssetsSection />,
      },
      {
        path: ":asset_uuid",
        element: <Navigate to="overview" replace />,
      },
      {
        path: ":asset_uuid/:active_tab",
        element: <LabAssetDetails />,
      },
    ],
  },
  {
    path: "software",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <SoftwareSection />,
      },
    ],
  },
  {
    path: "equipment",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <EquipmentSection />,
      },
    ],
  },
  {
    path: "",
    element: <DashboardLayout />,
    children: [
      {
        path: "team/users/:user_id/user-details",
        element: <UserDetails />,
      },
      {
        path: "team",
        element: <TeamSection />,
        children: [
          {
            path: "",
            element: <Navigate to="users" replace />,
          },
          {
            path: "users",
            element: <UsersTabContent />,
          },
          {
            path: "roles",
            element: <UserRoles />,
          },
          {
            path: "roles/:uuid",
            element: <UserRoles />,
          },
          {
            path: "groups",
            element: <GroupsTabContent />,
          },
        ],
      },
    ],
  },
  {
    path: "offices",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <OfficesSection />,
      },
      {
        path: ":office_id",
        element: <OfficeDetails />,
      },
    ],
  },
  {
    path: "reports",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Reports />,
      },
      {
        path: ":report_id",
        element: <ReportDetails />,
      },
    ],
  },
  {
    path: "",
    element: <DashboardLayout />,
    children: [
      {
        path: "settings",
        element: <MonolithSettings />,
        children: [
          {
            path: "",
            element: <Navigate to="system" replace />,
          },
          {
            path: "system",
            element: <SystemSettings />,
          },
          {
            path: "notifications",
            element: <NotificationSettings />,
          },
          {
            path: "org-info",
            element: <OrgInfoSettings />,
          },
          {
            path: "editor-templates",
            element: <EditorTemplatesSection />,
          },
          {
            path: "editor-templates/create",
            element: <CreateEditorTemplate />,
          },
          {
            path: "editor-templates/edit/:uuid",
            element: <TemplateEditor />,
          },
          {
            path: "document-templates",
            element: <DocumentTemplatesSection />,
          },
          {
            path: "relay",
            element: <RelaySettingsPage />,
            children: [
              {
                path: "",
                element: <Navigate to="basic-details" />,
              },
              {
                path: "basic-details",
                element: <BasicDetails />,
              },
              {
                path: "user-management",
                element: <UserManagement />,
                children: [
                  {
                    path: "users",
                    element: <RelayUsers />,
                  },
                  {
                    path: "users/:user_id",
                    element: <ProfileContent />,
                  },
                  {
                    path: "access-requests",
                    element: <AccessRequests />,
                  },
                ],
              },
              {
                path: "instructions",
                element: <Instructions />,
              },
              {
                path: "custom-field-options",
                element: <CustomFieldOptions />,
              },
            ],
          },
          {
            path: "item-numbers",
            element: <ItemNumberFormatSettings />,
          },
          {
            path: "case-types",
            element: <CaseTypeSettings />,
          },
          {
            path: "case-status",
            element: <CaseStatusSettings />,
          },
          {
            path: "case-progress",
            element: <CaseProgressSettings />,
          },
          {
            path: "evidence-types",
            element: <EvidenceTypeSettings />,
          },
          {
            path: "evidence-progress",
            element: <EvidenceProgressSettings />,
          },
          {
            path: "item-labels",
            element: <ItemLabelSettings />,
          },
          {
            path: "custom-fields",
            element: <CustomFieldSettings />,
          },
          {
            path: "task-templates",
            element: <TaskTemplateSettings />,
          },
          {
            path: "time-categories",
            element: <TimeCategorySettings />,
          },
          {
            path: "qa-checklists",
            element: <QaCheckListSettings />,
          },
          {
            path: "qa-checklists/:checklist_id",
            element: <QaCheckList />,
          },
          {
            path: "qa-issue-types",
            element: <QaIssueTypeSettings />,
          },
          {
            path: "admin-log",
            element: <AdminLog />,
          },
          {
            path: "integrations",
            element: <IntegrationSettings />,
            children: [
              { path: "", element: <IntegrationsList /> },
              { path: "forensic-partner-reports", element: <FprIntegration /> },
              { path: "mattermost", element: <MattermostIntegration /> },
              { path: "m365", element: <M365IntegrationPage /> },
              {
                path: "m365/client-credentials",
                element: <M365ClientCredentialsPage />,
              },
              { path: "google", element: <GoogleIntegration /> },
              {
                path: "google/service-account-credentials",
                element: <GoogleServiceAccountCredentialsPage />,
              },
            ],
          },
          {
            path: "api",
            children: [
              { path: "", element: <APIPage /> },
              { path: "webhooks/new", element: <CreateNewWebhookPage /> },
              { path: "webhooks/:uuid", element: <WebhookDetailsPage /> },
              { path: "webhooks/:uuid/edit", element: <EditWebhookPage /> },
            ],
          },
          {
            path: "release-notes",
            element: <ReleaseNotesSection />,
          },
          {
            path: "about",
            element: <About />,
          },
        ],
      },
    ],
  },
].map((route) => {
  route.element = <AuthGuard>{route.element}</AuthGuard>;
  return route;
});

const routes = [...unprotectedRoutes, ...protectedRoutes];

export default routes;
